import React from 'react';
import { Link, useLoaderData } from '@remix-run/react';
import type { loader as homeLoader } from '~/routes/_app._index';
import { ReactComponent as ArrowRight } from '@rocco/icons/svg/arrow/arrow-right.svg';
import logoWhite from '@rocco/icons/png/logo-white.png';
import { VideoBackground } from '@rocco/ui/banner/components/VideoBackground';
import { Image } from '@rocco/components/image';
import { TextRotate } from '@rocco/components/text-rotate';
import { Button as RoccoButton } from '@rocco/components/button';
import { useCTAHandlers } from '~/ui-rocco/hooks/use-home-cta-actions';
export const HomePageHero = () => {
    const { heroContent, appDisplayMode } = useLoaderData<typeof homeLoader>();

    const { handleCTAClick, handleLoginClick } = useCTAHandlers('HomePageHero');
    const isMobile = appDisplayMode === 'mobile';
    const { content, video } = heroContent;
    // Create a memoized background element to prevent re-renders
    const backgroundElement = React.useMemo(() => {
        if (video?.videoMp4 || video?.videoWebM) {
            return (
                <VideoBackground
                    {...video}
                    overlay={false}
                    backgroundTheme="transparent"
                />
            );
        }

        if (video?.videoCover) {
            return (
                <Image
                    src={video.videoCover}
                    overrideSrc={video.videoCover}
                    alt="Hero Image"
                    fill={true}
                    fit="cover"
                    priority={true} // Add priority to preload the image
                    loading="eager" // Force eager loading
                    quality={100}
                    className="h-auto max-w-full"
                    style={{
                        objectFit: 'cover',
                        imageRendering: 'pixelated',
                    }}
                />
            );
        }

        return null;
    }, [video]);

    const isLoginButton =
        ['/login', '/login/'].includes(
            content?.CTAButton?.link?.toLowerCase() ?? '',
        ) || !content?.CTAButton?.link; // If no link is provided, default to login button

    return (
        <>
            <section className="relative flex h-[55rem] justify-start overflow-hidden bg-black md:h-[67rem]">
                <div className="z-1 qhd:mx-auto qhd:w-full qhd:max-w-[1920px]">
                    <div className="flex h-full w-full flex-col justify-end px-6 pb-27 md:mt-[15rem] md:w-[51.125rem] md:justify-start md:pl-12 md:pt-32">
                        <h1
                            aria-label="Your beautiful home begins with Kieran"
                            className="m-0 max-w-full pb-17 text-14 font-medium leading-[116%] tracking-[0.03744rem] text-white max-md:font-display md:pb-[3.2rem] md:text-20 md:font-normal md:leading-[110%] md:tracking-[-0.15rem]"
                            style={{
                                WebkitTextSizeAdjust: '100%',
                                textSizeAdjust: '100%',
                            }}
                        >
                            {isMobile ? (
                                <TextRotate
                                    texts={content?.rotateTexts || []}
                                    prefixText={`${content?.prefix} `}
                                    subfixText={content?.suffix}
                                    pauseDuration={0.5}
                                    rotationDuration={4}
                                />
                            ) : (
                                <>
                                    <TextRotate
                                        texts={content?.rotateTexts || []}
                                        prefixText={`${content?.prefix} `}
                                        pauseDuration={0.5}
                                        rotationDuration={4}
                                    />
                                    <span>{`${content?.suffix}`}</span>
                                </>
                            )}
                        </h1>
                        {!isMobile && (
                            <p className="pb-12 pr-24 text-4 leading-[130%] tracking-[0.025rem] text-white md:w-144 md:pb-[3.2rem] md:pr-0 md:text-5">
                                {content?.description}
                            </p>
                        )}
                        <div className="items-center gap-9 md:flex">
                            <RoccoButton
                                color="glassy"
                                size={'larger'}
                                inverted={true}
                                asChild
                                onClick={() => {
                                    handleCTAClick('startProject');
                                    if (isLoginButton) {
                                        handleLoginClick();
                                    }
                                }}
                            >
                                {isLoginButton ? (
                                    <span>
                                        {content?.CTAButton?.text ||
                                            (isMobile
                                                ? 'Get Started'
                                                : 'Start Your Project')}
                                        <ArrowRight className="ml-2.5 w-5" />
                                    </span>
                                ) : (
                                    <Link
                                        to={
                                            content?.CTAButton?.link ?? '/login'
                                        }
                                        className="md:pl-[2.125rem] md:tracking-[0.035rem]"
                                    >
                                        {content?.CTAButton?.text ||
                                            'Start Your Project'}
                                        <ArrowRight className="ml-2.5 w-5" />
                                    </Link>
                                )}
                            </RoccoButton>
                        </div>
                    </div>
                </div>
                {backgroundElement}

                {/* png is smoother in chrome than svg */}
                <img
                    src={logoWhite}
                    alt="ArchiPro Logo"
                    className="absolute left-6 top-33 h-auto w-56 fill-white md:bottom-[26%] md:left-unset md:right-[10.5%] md:top-unset md:w-175 min-[2600px]:hidden"
                />
            </section>
            {isMobile && (
                <div className="px-4 pb-8 pt-13.5">
                    <p className="text-base">{content?.description}</p>
                </div>
            )}
        </>
    );
};
